import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Layout,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { selectPurchaseHistory } from '../../../../state/domain/purchases/purchaseHistoryReducer';
import '../bynx.css';
import PurchasesTable from '../../purchase/PurchasesTable';
import { selectPurchaseRanges, selectRequestPurchaseHistoryForPackageId } from '../../../../state/ui/configuration/packages/packageUiReducer';
import { requestPurchaseHistoryForPackage } from '../../../../state/ui/configuration/packages/packageUiActions';
import { adminRoutes } from '../../../../utility/route-locations';

/**
 * Displays the details of the package.
 */
function CurrentDetails({ currentPackage }) {
  if (!currentPackage) {
    return (
      <></>
    );
  }
  return (
    <div className="credit-enquiry--user-details">
      <div className="user-result--keywords">
        <div>
          <span>
            <b>
              <FormattedMessage id="price" />
            </b>
            :&nbsp;
          </span>
          <span>
            {`${currentPackage.price} ${currentPackage.currencyCode}`}
          </span>
        </div>
        <div>
          <span>
            <b>
              <FormattedMessage id="credits" />
            </b>
            :&nbsp;
          </span>
          <span>
            {currentPackage.mainTokens}
          </span>
        </div>
        <div>
          <span>
            <b>
              <FormattedMessage id="bonusCredits" />
            </b>
            :&nbsp;
          </span>
          <span>
            {currentPackage.extraTokens}
          </span>
        </div>
      </div>
    </div>
  );
}

CurrentDetails.propTypes = {
  currentPackage: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currencyCode: PropTypes.string.isRequired,
    mainTokens: PropTypes.number.isRequired,
    extraTokens: PropTypes.number.isRequired,
  }),
};

CurrentDetails.defaultProps = {
  currentPackage: null,
};

/**
 * A component allowing a bynx admin user to retrive the purchase history for a package.
 */
function PackagePurchases({
  isLoading, loadPageData, purchases, packageDateRangeMap, match,
}) {
  const { id } = match.params;

  useEffect(() => {
    loadPageData(id, packageDateRangeMap[id]);
  }, [loadPageData, id, packageDateRangeMap]);

  const title = purchases[0] ? purchases[0].packageTitle : null;
  return (
    <Layout className="via-centre via-layout-configure-main">
      <ConfigHeading
        title={title}
        titleId={!title && isLoading ? 'loading' : 'noSales'}
        links={[(
          <Link to={adminRoutes.admin_package_sales}>
            <FormattedMessage id="returnToSalesSummary" />
          </Link>
        )]}
      >
        <CurrentDetails currentPackage={purchases[0]} />
        <PurchasesTable
          isLoading={isLoading}
          purchases={purchases}
          includeCompanyName
          includePackageDetails={false}
        />
      </ConfigHeading>
    </Layout>
  );
}

// Validate the prop type parameters.
PackagePurchases.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  purchases: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      purchaseDateTime: PropTypes.string.isRequired,
      packageTitle: PropTypes.string.isRequired,
      purchaseStatus: PropTypes.string.isRequired,
      currencyCode: PropTypes.string.isRequired,
      mainTokens: PropTypes.number.isRequired,
      extraTokens: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      tokensRemaining: PropTypes.number.isRequired,
      vatCharge: PropTypes.number,
      expiryDate: PropTypes.string,
    }),
  ),
  packageDateRangeMap: PropTypes.objectOf(
    PropTypes.shape({
      toDate: PropTypes.string,
      fromDate: PropTypes.string,
    }),
  ).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

PackagePurchases.defaultProps = {
  isLoading: false,
  purchases: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectRequestPurchaseHistoryForPackageId(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    purchases: selectPurchaseHistory(state),
    packageDateRangeMap: selectPurchaseRanges(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (groupId, packageDateRange) => dispatch(requestPurchaseHistoryForPackage(groupId, packageDateRange)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(PackagePurchases);
